import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowLeft, Clock, Calendar, User, Moon, Smartphone, Coffee, Bath, BookOpen, Thermometer, Music, Brain, Heart, Sparkles, CheckCircle2, AlertTriangle, Lightbulb } from "lucide-react";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import CTACard from "@/components/blog/CTACard";
import BlogImage from "@/components/blog/BlogImage";
import ScrollToTopButton from "@/components/ScrollToTopButton";
import { getPostBySlug } from "@/data/blogPosts";
const RotinaNortuna = () => {
  const post = getPostBySlug("rotina-noturna-para-dormir-melhor");

  if (!post) return null;

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>{post.metaTitle}</title>
        <meta name="description" content={post.metaDescription} />
        <meta name="keywords" content="rotina noturna, dormir melhor, qualidade do sono, insônia, higiene do sono, relaxamento, melatonina, sono profundo" />
        <link rel="canonical" href="https://habitfrog.com/blog/rotina-noturna-para-dormir-melhor" />
        <meta property="og:title" content={post.metaTitle} />
        <meta property="og:description" content={post.metaDescription} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://habitfrog.com/blog/rotina-noturna-para-dormir-melhor" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.metaTitle} />
        <meta name="twitter:description" content={post.metaDescription} />
      </Helmet>

      <Header />
      <ScrollToTopButton />

      <main className="pt-24 pb-16">
        <div className="container mx-auto px-4">
          {/* Breadcrumb */}
          <nav className="mb-8" aria-label="Breadcrumb">
            <Link to="/home" className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors">
              <ArrowLeft className="w-4 h-4" />
              Voltar ao início
            </Link>
          </nav>

          {/* Article Header */}
          <header className="max-w-4xl mx-auto mb-12">
            <span className="inline-block bg-indigo-500/20 text-indigo-600 dark:text-indigo-400 px-3 py-1 rounded-full text-sm font-semibold mb-4">
              {post.category}
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold text-foreground mb-6 leading-tight">
              {post.title}
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-muted-foreground">
              <div className="flex items-center gap-2">
                <User className="w-4 h-4" />
                <span>{post.author}</span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                <span>{post.date}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                <span>{post.readTime}</span>
              </div>
            </div>
          </header>

          {/* Article Content */}
          <article className="max-w-4xl mx-auto prose prose-lg max-w-none">
            
            {/* Introduction */}
            <div className="mb-12">
              <p className="text-xl text-muted-foreground leading-relaxed">
                Você já acordou mais cansado do que quando foi dormir? Passou horas revirando na cama, 
                olhando para o teto, enquanto sua mente não parava de pensar em tudo que precisava fazer 
                no dia seguinte? Se sim, você não está sozinho. Segundo pesquisas do Instituto do Sono, 
                <strong> mais de 73 milhões de brasileiros sofrem com problemas relacionados ao sono</strong>.
              </p>
            </div>

            {/* Grid de duas colunas */}
            <div className="grid md:grid-cols-2 gap-8 mb-12">
              <div>
                <p className="text-foreground/80">
                  A boa notícia é que existe uma solução simples, gratuita e cientificamente comprovada: 
                  criar uma rotina noturna consistente. Neste guia completo, você vai descobrir exatamente 
                  como preparar seu corpo e mente para uma noite de sono reparador.
                </p>
                <p className="text-foreground/80">
                  Este artigo não é apenas uma lista de dicas superficiais. Vamos mergulhar profundamente 
                  na ciência do sono, entender por que cada hábito funciona, e criar um plano prático 
                  que você pode começar a implementar ainda hoje.
                </p>
              </div>
              <div>
                <p className="text-foreground/80">
                  Para criar uma rotina noturna eficaz, primeiro precisamos entender o que acontece 
                  quando dormimos. O sono não é simplesmente "desligar" - é um processo ativo e 
                  essencial onde nosso corpo realiza funções vitais.
                </p>
                <p className="text-foreground/80">
                  O problema da vida moderna? <strong>Estamos constantemente expostos à luz artificial</strong>, 
                  especialmente a luz azul de telas, que confunde nosso relógio biológico e suprime 
                  a produção de melatonina.
                </p>
              </div>
            </div>

            <BlogImage 
              src="/images/blog/quarto-noite-sono.jpg" 
              alt="Ambiente noturno relaxante com iluminação suave propício para dormir bem e ter uma rotina noturna saudável" 
              className="h-64 md:h-80 mb-12"
              priority
            />

            <p className="text-lg text-foreground/90 mb-12 bg-accent/10 p-6 rounded-xl border-l-4 border-primary">
              <strong>Pronto para transformar suas noites?</strong> Confira a seguir os 7 pilares de uma rotina noturna perfeita, 
              baseados em evidências científicas sólidas e dicas práticas que você pode aplicar hoje mesmo.
            </p>

            {/* Personal Story */}
            <section className="mb-16">
              <h2 className="text-3xl font-bold text-foreground mb-6 flex items-center gap-4">
                <span className="w-12 h-12 rounded-full bg-rose-500/20 text-rose-500 flex items-center justify-center text-xl">
                  <Heart className="w-6 h-6" />
                </span>
                A História de Ana: De Insone a Dorminhoca Feliz
              </h2>

              <div className="grid md:grid-cols-2 gap-8 mb-8">
                <div>
                  <p className="text-foreground/80 mb-4">
                    Ana, 34 anos, executiva de marketing em São Paulo, chegou ao consultório médico em 
                    desespero. Há três anos ela não conseguia dormir mais de 4 horas por noite. Tomava 
                    três cafés depois das 18h, respondia e-mails até meia-noite, e o celular ficava 
                    ao lado do travesseiro "para emergências".
                  </p>
                  <p className="text-foreground/80 mb-4">
                    "Eu achava que estava sendo produtiva", conta Ana. "Na verdade, estava destruindo 
                    minha saúde, meus relacionamentos e até minha carreira. Cometi erros graves no 
                    trabalho por falta de concentração."
                  </p>
                </div>
                <div>
                  <p className="text-foreground/80 mb-4">
                    O ponto de virada veio quando Ana quase dormiu ao volante, voltando do trabalho. 
                    "Percebi que precisava mudar radicalmente. Não era sobre produtividade - era 
                    sobre sobrevivência."
                  </p>
                  <p className="text-foreground/80">
                    Com a ajuda de um especialista em sono e muita determinação, Ana desenvolveu uma 
                    rotina noturna que transformou sua vida. Em apenas 8 semanas, ela passou a dormir 
                    7-8 horas por noite, acordando naturalmente antes do despertador, cheia de energia.
                  </p>
                </div>
              </div>
            </section>

            {/* Science of Sleep */}
            <section className="mb-16">
              <h2 className="text-3xl font-bold text-foreground mb-6 flex items-center gap-4">
                <span className="w-12 h-12 rounded-full bg-purple-500/20 text-purple-500 flex items-center justify-center text-xl">
                  <Brain className="w-6 h-6" />
                </span>
                A Ciência Por Trás do Sono
              </h2>

              <div className="grid md:grid-cols-2 gap-8 items-center mb-8">
                <div>
                  <p className="text-foreground/80 mb-4">
                    Durante a noite, passamos por 4-6 ciclos de sono, cada um durando aproximadamente 
                    90 minutos. Cada ciclo inclui diferentes estágios essenciais para a regeneração 
                    do corpo e da mente.
                  </p>
                  <p className="text-foreground/80">
                    Nosso corpo possui um relógio biológico interno de 24 horas chamado ritmo circadiano. 
                    Este relógio é controlado principalmente pela luz - quando escurece, o cérebro 
                    começa a produzir melatonina, o hormônio do sono.
                  </p>
                </div>
                <BlogImage 
                  src="/images/blog/ciclos-sono.jpg" 
                  alt="Representação visual dos ciclos do sono e fases REM para entender a ciência por trás do sono reparador" 
                  className="h-64"
                />
              </div>

              <div className="grid md:grid-cols-4 gap-4 mb-8">
                <div className="bg-card p-4 rounded-xl border text-center">
                  <div className="text-3xl mb-2">😴</div>
                  <h4 className="font-semibold text-sm">Sono Leve</h4>
                  <p className="text-xs text-muted-foreground">Transição, fácil acordar</p>
                </div>
                <div className="bg-card p-4 rounded-xl border text-center">
                  <div className="text-3xl mb-2">💪</div>
                  <h4 className="font-semibold text-sm">Sono Profundo</h4>
                  <p className="text-xs text-muted-foreground">Regeneração física</p>
                </div>
                <div className="bg-card p-4 rounded-xl border text-center">
                  <div className="text-3xl mb-2">💭</div>
                  <h4 className="font-semibold text-sm">Sono REM</h4>
                  <p className="text-xs text-muted-foreground">Sonhos, memória</p>
                </div>
                <div className="bg-card p-4 rounded-xl border text-center">
                  <div className="text-3xl mb-2">🔄</div>
                  <h4 className="font-semibold text-sm">Ciclos</h4>
                  <p className="text-xs text-muted-foreground">4-6 por noite</p>
                </div>
              </div>
            </section>

            <div className="my-16"><CTACard variant="primary" /></div>

            {/* The 7 Pillars */}
            <section className="mb-16">
              <h2 className="text-3xl font-bold text-foreground mb-6 flex items-center gap-4">
                <span className="w-12 h-12 rounded-full bg-indigo-500/20 text-indigo-500 flex items-center justify-center text-xl">
                  <Moon className="w-6 h-6" />
                </span>
                Os 7 Pilares de uma Rotina Noturna Perfeita
              </h2>

              {/* Pilar 1 */}
              <div className="bg-card p-8 rounded-2xl border mb-8">
                <h3 className="text-2xl font-bold text-foreground mb-4 flex items-center gap-3">
                  <span className="w-10 h-10 rounded-full bg-amber-500 text-white flex items-center justify-center font-bold">1</span>
                  <Coffee className="w-6 h-6 text-amber-600" />
                  Corte a Cafeína no Horário Certo
                </h3>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <p className="text-foreground/80 mb-4">
                      A cafeína tem uma meia-vida de 5-6 horas, o que significa que se você toma 
                      um café às 16h, às 22h ainda terá metade da cafeína circulando no seu corpo.
                    </p>
                    <div className="bg-amber-500/10 p-4 rounded-lg border border-amber-500/30">
                      <p className="text-sm font-medium text-foreground">
                        💡 <strong>Regra Prática:</strong> Evite cafeína pelo menos 8 horas antes de dormir. 
                        Se você dorme às 22h, sua última xícara de café deve ser antes das 14h.
                      </p>
                    </div>
                  </div>
                  <div>
                    <p className="text-foreground/80">
                      <strong>Fontes ocultas de cafeína:</strong> Chá preto, chá verde, chocolate, 
                      refrigerantes de cola, alguns medicamentos para dor de cabeça, e energéticos. 
                      Leia os rótulos com atenção.
                    </p>
                  </div>
                </div>
              </div>

              {/* Pilar 2 */}
              <div className="bg-card p-8 rounded-2xl border mb-8">
                <h3 className="text-2xl font-bold text-foreground mb-4 flex items-center gap-3">
                  <span className="w-10 h-10 rounded-full bg-blue-500 text-white flex items-center justify-center font-bold">2</span>
                  <Smartphone className="w-6 h-6 text-blue-600" />
                  Desconecte das Telas
                </h3>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <p className="text-foreground/80 mb-4">
                      A luz azul emitida por smartphones, tablets, computadores e TVs suprime a 
                      produção de melatonina em até 50%. O conteúdo que consumimos estimula o cérebro 
                      exatamente quando deveria estar relaxando.
                    </p>
                  </div>
                  <div className="bg-blue-500/10 p-4 rounded-lg border border-blue-500/30">
                    <h4 className="font-bold text-foreground mb-2">Estratégias Práticas:</h4>
                    <ul className="list-disc pl-5 space-y-1 text-sm text-muted-foreground">
                      <li>Toque de recolher digital 60-90 min antes de dormir</li>
                      <li>Mode noturno em todos os dispositivos</li>
                      <li>Carregue o celular fora do quarto</li>
                      <li>Substitua scroll por atividades relaxantes</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Pilar 3 */}
              <div className="bg-card p-8 rounded-2xl border mb-8">
                <h3 className="text-2xl font-bold text-foreground mb-4 flex items-center gap-3">
                  <span className="w-10 h-10 rounded-full bg-teal-500 text-white flex items-center justify-center font-bold">3</span>
                  <Bath className="w-6 h-6 text-teal-600" />
                  O Ritual do Banho Quente
                </h3>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <p className="text-foreground/80 mb-4">
                      Um dos truques mais eficazes para adormecer rápido é tomar um banho quente 
                      1-2 horas antes de deitar. Quando você sai do banho, a temperatura do corpo 
                      cai rapidamente, sinalizando ao cérebro que é hora de dormir.
                    </p>
                  </div>
                  <div className="bg-teal-500/10 p-4 rounded-lg border border-teal-500/30">
                    <p className="text-sm font-medium text-foreground">
                      🛁 <strong>A Ciência:</strong> Estudos mostram que pessoas que tomam banho quente 
                      antes de dormir pegam no sono, em média, <strong>36% mais rápido</strong>.
                      Temperatura ideal: 40-43°C por 10-15 minutos.
                    </p>
                  </div>
                </div>
              </div>

              {/* Pilar 4 */}
              <div className="bg-card p-8 rounded-2xl border mb-8">
                <h3 className="text-2xl font-bold text-foreground mb-4 flex items-center gap-3">
                  <span className="w-10 h-10 rounded-full bg-violet-500 text-white flex items-center justify-center font-bold">4</span>
                  <Thermometer className="w-6 h-6 text-violet-600" />
                  Otimize Seu Ambiente de Sono
                </h3>
                <div className="grid md:grid-cols-4 gap-4">
                  <div className="bg-violet-500/10 p-4 rounded-lg border border-violet-500/30 text-center">
                    <div className="text-2xl mb-2">🌡️</div>
                    <h4 className="font-bold text-sm mb-1">Temperatura</h4>
                    <p className="text-xs text-muted-foreground">18-21°C ideal</p>
                  </div>
                  <div className="bg-violet-500/10 p-4 rounded-lg border border-violet-500/30 text-center">
                    <div className="text-2xl mb-2">🌑</div>
                    <h4 className="font-bold text-sm mb-1">Escuridão</h4>
                    <p className="text-xs text-muted-foreground">Blackout, sem LEDs</p>
                  </div>
                  <div className="bg-violet-500/10 p-4 rounded-lg border border-violet-500/30 text-center">
                    <div className="text-2xl mb-2">🔇</div>
                    <h4 className="font-bold text-sm mb-1">Silêncio</h4>
                    <p className="text-xs text-muted-foreground">Ruído branco ajuda</p>
                  </div>
                  <div className="bg-violet-500/10 p-4 rounded-lg border border-violet-500/30 text-center">
                    <div className="text-2xl mb-2">🛏️</div>
                    <h4 className="font-bold text-sm mb-1">Conforto</h4>
                    <p className="text-xs text-muted-foreground">Colchão e travesseiros</p>
                  </div>
                </div>
              </div>

              {/* Pilar 5 */}
              <div className="bg-card p-8 rounded-2xl border mb-8">
                <h3 className="text-2xl font-bold text-foreground mb-4 flex items-center gap-3">
                  <span className="w-10 h-10 rounded-full bg-rose-500 text-white flex items-center justify-center font-bold">5</span>
                  <BookOpen className="w-6 h-6 text-rose-600" />
                  Pratique o Descarregamento Mental
                </h3>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <p className="text-foreground/80 mb-4">
                      Uma das maiores causas de insônia é a mente acelerada. Pensamentos sobre o 
                      dia, preocupações sobre o amanhã, listas de tarefas mentais - tudo isso 
                      impede o relaxamento necessário para dormir.
                    </p>
                  </div>
                  <div className="bg-rose-500/10 p-4 rounded-lg border border-rose-500/30">
                    <h4 className="font-bold text-foreground mb-2">Técnicas:</h4>
                    <ul className="list-disc pl-5 space-y-1 text-sm text-muted-foreground">
                      <li><strong>Journaling:</strong> 3 gratidões + 3 prioridades do dia seguinte</li>
                      <li><strong>Brain dump:</strong> 5 min escrevendo tudo que está na mente</li>
                      <li><strong>Reflexão positiva:</strong> 3 momentos bons do dia</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Pilar 6 */}
              <div className="bg-card p-8 rounded-2xl border mb-8">
                <h3 className="text-2xl font-bold text-foreground mb-4 flex items-center gap-3">
                  <span className="w-10 h-10 rounded-full bg-green-500 text-white flex items-center justify-center font-bold">6</span>
                  <Music className="w-6 h-6 text-green-600" />
                  Técnicas de Relaxamento Ativo
                </h3>
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="bg-green-500/10 p-4 rounded-lg border border-green-500/30">
                    <h4 className="font-bold text-foreground mb-2">Respiração 4-7-8</h4>
                    <ol className="list-decimal pl-5 space-y-1 text-sm text-muted-foreground">
                      <li>Inspire pelo nariz contando até 4</li>
                      <li>Segure a respiração contando até 7</li>
                      <li>Expire pela boca contando até 8</li>
                      <li>Repita 4 vezes</li>
                    </ol>
                  </div>
                  <div className="bg-green-500/10 p-4 rounded-lg border border-green-500/30">
                    <h4 className="font-bold text-foreground mb-2">Relaxamento Muscular</h4>
                    <p className="text-sm text-muted-foreground">
                      Começando pelos pés, tensione cada grupo muscular por 5 segundos e depois 
                      relaxe completamente. Suba pelo corpo até o rosto.
                    </p>
                  </div>
                </div>
              </div>

              {/* Pilar 7 */}
              <div className="bg-card p-8 rounded-2xl border mb-8">
                <h3 className="text-2xl font-bold text-foreground mb-4 flex items-center gap-3">
                  <span className="w-10 h-10 rounded-full bg-sky-500 text-white flex items-center justify-center font-bold">7</span>
                  <Clock className="w-6 h-6 text-sky-600" />
                  Consistência é Tudo
                </h3>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <p className="text-foreground/80 mb-4">
                      O pilar mais importante de todos: manter horários consistentes. Seu corpo 
                      ama previsibilidade. Quando você dorme e acorda nos mesmos horários todos 
                      os dias (inclusive fins de semana), seu ritmo circadiano se fortalece.
                    </p>
                  </div>
                  <div className="bg-sky-500/10 p-4 rounded-lg border border-sky-500/30">
                    <p className="text-sm font-medium text-foreground">
                      ⏰ <strong>Regra de Ouro:</strong> Não varie mais de 30 minutos no horário de 
                      dormir e acordar, mesmo nos fins de semana. O "jet lag social" de dormir 
                      até tarde no domingo atrapalha toda a semana seguinte.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Sample Routine */}
            <section className="mb-16">
              <h2 className="text-3xl font-bold text-foreground mb-6 flex items-center gap-4">
                <span className="w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xl">
                  <Sparkles className="w-6 h-6" />
                </span>
                Exemplo de Rotina Noturna Completa
              </h2>

              <div className="bg-gradient-to-br from-primary/10 to-accent/10 p-8 rounded-2xl border">
                <h3 className="text-xl font-bold text-center mb-6">Rotina para Dormir às 22h30</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  {[
                    { time: "20h00", title: "Última refeição leve", desc: "Evitar gorduras e açúcar" },
                    { time: "20h30", title: "Banho quente", desc: "10-15 min com sais de Epsom" },
                    { time: "21h00", title: "Desconexão digital", desc: "Celular fora do quarto" },
                    { time: "21h15", title: "Preparar dia seguinte", desc: "Roupa, mochila, 3 prioridades" },
                    { time: "21h30", title: "Journaling", desc: "3 gratidões, brain dump" },
                    { time: "21h45", title: "Leitura leve", desc: "20-30 min, livro físico" },
                    { time: "22h15", title: "Relaxamento", desc: "Respiração 4-7-8" },
                    { time: "22h30", title: "💤 Sono", desc: "Quarto escuro, mente tranquila" },
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-4 p-3 bg-background/50 rounded-lg">
                      <div className={`w-14 h-10 rounded-full flex items-center justify-center text-xs font-bold text-white ${i === 7 ? 'bg-green-500' : 'bg-primary'}`}>
                        {item.time}
                      </div>
                      <div>
                        <h4 className="font-bold text-sm">{item.title}</h4>
                        <p className="text-xs text-muted-foreground">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* Common Mistakes */}
            <section className="mb-16">
              <h2 className="text-3xl font-bold text-foreground mb-6 flex items-center gap-4">
                <span className="w-12 h-12 rounded-full bg-amber-500/20 text-amber-500 flex items-center justify-center text-xl">
                  <AlertTriangle className="w-6 h-6" />
                </span>
                Erros Comuns Que Sabotam Seu Sono
              </h2>

              <div className="grid md:grid-cols-2 gap-4">
                {[
                  { title: '"Recuperar" sono no fim de semana', desc: 'Dormir até tarde bagunça seu ritmo circadiano por dias.' },
                  { title: 'Exercícios intensos à noite', desc: 'Elevação da temperatura e adrenalina prejudicam o relaxamento.' },
                  { title: 'Álcool como "ajuda" para dormir', desc: 'Fragmenta o sono e suprime o sono REM. Acorda cansado.' },
                  { title: 'Ficar na cama acordado', desc: 'Cria associação negativa. Levante-se após 20 min sem dormir.' },
                  { title: '"Só mais um episódio"', desc: 'Binge-watching é um dos maiores ladrões de sono.' },
                  { title: 'Jantar pesado tarde da noite', desc: 'A digestão interfere no sono. Prefira jantares leves.' },
                ].map((item, i) => (
                  <div key={i} className="bg-red-50 dark:bg-red-950/20 border-l-4 border-red-500 p-4 rounded-r-lg">
                    <h3 className="font-bold text-red-700 dark:text-red-300 text-sm mb-1">❌ {item.title}</h3>
                    <p className="text-xs text-muted-foreground">{item.desc}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* Action Plan */}
            <section className="mb-16">
              <h2 className="text-3xl font-bold text-foreground mb-6 flex items-center gap-4">
                <span className="w-12 h-12 rounded-full bg-green-500/20 text-green-500 flex items-center justify-center text-xl">
                  <CheckCircle2 className="w-6 h-6" />
                </span>
                Seu Plano de Ação: 4 Semanas
              </h2>

              <div className="grid md:grid-cols-2 gap-4 mb-8">
                {[
                  { week: "Semana 1: Fundação", items: ["Horários fixos de dormir e acordar", "Corte cafeína depois das 14h", "Celular fora do quarto"] },
                  { week: "Semana 2: Ambiente", items: ["Temperatura 18-21°C", "Melhore a escuridão", "Toque de recolher digital 1h antes"] },
                  { week: "Semana 3: Ritual", items: ["Banho quente à rotina", "Journaling noturno", "Respiração 4-7-8"] },
                  { week: "Semana 4: Refinamento", items: ["Leitura leve", "Relaxamento muscular", "Avalie e ajuste"] },
                ].map((item, i) => (
                  <div key={i} className="bg-green-50 dark:bg-green-950/20 border-l-4 border-green-500 p-4 rounded-r-lg">
                    <h3 className="font-bold text-green-700 dark:text-green-300 text-sm mb-2">{item.week}</h3>
                    <ul className="list-disc pl-5 space-y-1 text-xs text-muted-foreground">
                      {item.items.map((li, j) => <li key={j}>{li}</li>)}
                    </ul>
                  </div>
                ))}
              </div>

              <div className="bg-primary/10 p-6 rounded-xl border border-primary/30">
                <h3 className="text-xl font-bold text-foreground mb-3 flex items-center gap-2">
                  🐸 Dica HabitFrog
                </h3>
                <p className="text-muted-foreground">
                  Use o HabitFrog para rastrear sua rotina noturna. Crie um hábito "Rotina de Sono" 
                  e marque como completo cada noite que seguir seu ritual. Veja seu sapo evoluir 
                  enquanto sua qualidade de sono melhora!
                </p>
              </div>
            </section>

            {/* Conclusion */}
            <section className="mb-16">
              <h2 className="text-3xl font-bold text-foreground mb-6 flex items-center gap-4">
                <span className="w-12 h-12 rounded-full bg-indigo-500/20 text-indigo-500 flex items-center justify-center text-xl">
                  <Moon className="w-6 h-6" />
                </span>
                Conclusão: O Sono Como Superpoder
              </h2>

              <p className="text-foreground/80 mb-4">
                Vivemos em uma cultura que glorifica a privação de sono. "Dormirei quando morrer" 
                é tratado como medalha de honra. Mas a ciência é clara: <strong>sono não é luxo, 
                é necessidade fundamental</strong>. Cada hora de sono de qualidade é um investimento 
                na sua saúde física, mental e emocional.
              </p>

              <p className="text-foreground/80 mb-4">
                A rotina noturna que você criar não é apenas sobre dormir melhor - é sobre 
                viver melhor. Mais energia, mais foco, mais criatividade, mais paciência, 
                melhor humor, menos estresse.
              </p>

              <p className="text-lg text-foreground/90 mb-8 bg-indigo-500/10 p-6 rounded-xl border-l-4 border-indigo-500 italic">
                "O sono é o melhor tratamento de beleza. É a melhor terapia. É a melhor forma de 
                aumentar produtividade. E é completamente grátis. Tudo que você precisa fazer é 
                criar as condições certas e deixar seu corpo fazer o resto."
                <br /><span className="text-sm font-medium not-italic">— Arianna Huffington</span>
              </p>

              <p className="text-foreground/80">
                Comece hoje. Não precisa ser perfeito. Escolha um pilar, implemente por uma 
                semana, depois adicione outro. Em um mês, você terá uma rotina noturna que 
                vai transformar não apenas suas noites, mas todos os seus dias.
              </p>
            </section>

            {/* Final CTA */}
            <div className="bg-gradient-to-r from-primary to-primary/80 rounded-2xl p-8 text-center text-white">
              <h3 className="text-2xl font-bold mb-3">Pronto para Dormir Melhor?</h3>
              <p className="mb-6 opacity-90">
                Comece a rastrear sua rotina noturna com o HabitFrog e veja seu sapo evoluir 
                enquanto você conquista noites de sono reparador.
              </p>
              <Link 
                to="/app" 
                className="inline-flex items-center px-8 py-3 bg-white text-primary rounded-full font-semibold hover:bg-white/90 transition-colors"
              >
                Começar Agora - É Grátis
              </Link>
            </div>
          </article>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default RotinaNortuna;
